import React, {
  useCallback, useEffect, useRef, useState,
} from 'lib/react';
import PropTypes from 'prop-types';
import './BingMap.css';
import { COMMERCIAL, RESIDENTIAL, PIN_COLORS } from 'constants/AppConstants';
import { createParcelTileLayer, loadBingApi } from './BingMap.utils';
import { EVENTS, MAP_DEFAULTS } from './BingMap.constants';
import {
  getMapZoomForPoint,
  getGeocodeViewKey,
  shouldApplyInitialMapView,
} from './mapView.utils';
import HtmlPushpinLayerModule from './components/HtmlPushpinLayerModule';

function BingMap(props) {
  const {
    mapOptions,
    mapPoints,
    isComplex,
    propertyType,
    onAddPin,
    onDeletePin,
    onUpdatePinLocation,
    onFocusChange,
    focusedPinIndex,
    pinDragEnabled,
    colors,
    mapDisclaimerOffsetHeight,
    defaultZoom,
    onFetchPinAddress,
  } = props;

  const mapRef = useRef();
  const map = useRef();
  const pushpinLayer = useRef();
  const retryTimeout = useRef();
  // #genai - Keep latest center in a ref so updateMapView always reads the current value
  const mapCenterRef = useRef(null);

  const [mapDisclaimerHeight, setMapDisclaimerHeight] = useState(0);
  // Source of truth for zoom so updateMapView never reads a stale currentZoom from a sibling effect.
  const currentZoomRef = useRef(null);
  const hasInitializedView = useRef(false);
  // #genai - Persist map center so pin moves do not reset the view
  const [mapCenter, setMapCenter] = useState(null);
  const appliedGeocodeViewKey = useRef('');

  const persistMapCenter = (center) => {
    if (!center) return;
    const nextCenter = {
      latitude: center.latitude,
      longitude: center.longitude,
    };
    mapCenterRef.current = nextCenter;
    setMapCenter(nextCenter);
  };

  const handleAddPin = (e, pinCount) => {
    const evt = e.type === EVENTS.TOUCHEND ? e.changedTouches[0] : e;
    const { left, top } = evt.target.getBoundingClientRect();
    const { Microsoft } = window;
    const point = new Microsoft.Maps.Point(evt.clientX - left, evt.clientY - top);
    const { latitude, longitude } = map.current.tryPixelToLocation(point, Microsoft.Maps.PixelReference.control);
    if (onAddPin) onAddPin(latitude, longitude, pinCount);
  };

  // #genai - Updated to use options object
  const updateMapPins = (addressList) => {
    pushpinLayer.current.setIsComplex(isComplex);
    pushpinLayer.current.setPushpins(addressList, {
      propertyType,
      handleDeletePin: onDeletePin,
      focusedPinIndex,
      pinDragEnabled,
      colors,
      handleFocusChange: onFocusChange,
      handleFetchPinAddress: onFetchPinAddress,
    });
  };

  const updateMapView = () => {
    const settings = {
      ...MAP_DEFAULTS,
      ...mapOptions,
    };

    const { center, zoom } = settings;
    const view = { center, zoom };

    if (mapPoints.length > 0 || mapOptions.center) {
      settings.minZoom = 12;
      const focusedMapPoint = mapPoints[Math.max(focusedPinIndex, 0)];
      // #genai - Prefer stored center; seed once from focused/first pin or mapOptions
      const storedCenter = mapCenterRef.current || mapCenter;
      if (storedCenter) {
        view.center = storedCenter;
      } else {
        const seedCenter = focusedMapPoint?.address || mapOptions.center;
        if (seedCenter) {
          view.center = seedCenter;
          persistMapCenter(seedCenter);
        }
      }

      // Zip fallback starts at zip-level zoom until the user moves the pin.
      const mapZoom = getMapZoomForPoint({
        usedZipFallback: focusedMapPoint?.usedZipFallback,
        userMovedThePin: focusedMapPoint?.userMovedThePin,
        structureZoom: defaultZoom,
      });
      const trackedZoom = currentZoomRef.current;
      view.zoom = trackedZoom >= settings.minZoom ? trackedZoom : mapZoom;
    }

    if (map.current) {
      map.current.setOptions(settings);
      map.current.setView(view);
    }
  };

  const initMap = useCallback(() => {
    const { Microsoft } = window;
    // #genai - optional chaining keeps the same guard for a missing Microsoft/Maps global
    if (!Microsoft?.Maps) throw new Error('Bing Maps API not loaded');

    const settings = {
      ...MAP_DEFAULTS,
      mapTypeId: Microsoft.Maps.MapTypeId.aerial,
    };

    try {
      map.current = new Microsoft.Maps.Map(mapRef.current, settings);

      const HtmlPushpinLayer = HtmlPushpinLayerModule(handleAddPin, onUpdatePinLocation, () => onFocusChange(null));
      pushpinLayer.current = new HtmlPushpinLayer();

      map.current.layers.insert(pushpinLayer.current);
      map.current.layers.insert(createParcelTileLayer(settings.minParcelLayerZoom));

      Microsoft.Maps.Events.addHandler(map.current, 'viewchange', () => {
        pushpinLayer.current.updatePositions();

        const zoomLevel = map.current.getZoom();
        currentZoomRef.current = zoomLevel;
        // #genai - Sync stored center as the user pans/zooms
        persistMapCenter(map.current.getCenter());
      });

      if (mapPoints && mapPoints.length > 0) {
        updateMapView();
        updateMapPins(mapPoints);
      }
    } catch (error) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        initMap();
      }, 1000);
    }
  }, []);

  // #genai - When focus changes, move stored center to that pin
  useEffect(() => {
    if (focusedPinIndex == null || !mapPoints[focusedPinIndex]?.address) return;
    persistMapCenter(mapPoints[focusedPinIndex].address);
  }, [focusedPinIndex]);

  useEffect(() => {
    const pin = mapPoints[0];
    const address = pin?.address;

    if (address) {
      const geocodeViewKey = getGeocodeViewKey({
        latitude: address.latitude,
        longitude: address.longitude,
      });
      const applyView = shouldApplyInitialMapView({
        hasInitializedView: hasInitializedView.current,
        userMovedThePin: pin.userMovedThePin,
        geocodeViewKey,
        appliedGeocodeViewKey: appliedGeocodeViewKey.current,
      });

      if (applyView) {
        appliedGeocodeViewKey.current = geocodeViewKey;
        hasInitializedView.current = true;
        currentZoomRef.current = null;
        persistMapCenter(address);
      }
    } else {
      appliedGeocodeViewKey.current = '';
      hasInitializedView.current = false;
      currentZoomRef.current = null;
    }

    if (map.current) {
      updateMapView();
      updateMapPins(mapPoints);
    }
    // #genai - mapCenter / zoom are read via refs so pin moves keep the current view
  }, [mapPoints, isComplex, propertyType, focusedPinIndex, pinDragEnabled, mapOptions]);

  useEffect(() => {
    loadBingApi().then(() => initMap(mapPoints, mapOptions));

    // Set the position of bottom overlay above MapDisclaimer
    const resizeObserver = new ResizeObserver(() => {
      const mapDisclaimerComponent = document.getElementById('map-disclaimer');
      setMapDisclaimerHeight(mapDisclaimerComponent?.offsetHeight || 0);
    });
    resizeObserver.observe(mapRef.current);

    return () => {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={mapRef}
      className="map"
      style={{ '--bottom-overlay-position': `${Math.max(mapDisclaimerHeight + mapDisclaimerOffsetHeight, 24)}px` }}
    />
  );
}

BingMap.defaultProps = {
  mapOptions: {},
  isComplex: false,
  onAddPin: null,
  onUpdatePinLocation: null,
  onDeletePin: null,
  focusedPinIndex: 0,
  pinDragEnabled: false,
  colors: PIN_COLORS,
  mapDisclaimerOffsetHeight: 7,
  defaultZoom: 17,
  onFetchPinAddress: null,
};

BingMap.propTypes = {
  mapOptions: PropTypes.shape(),
  mapPoints: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isComplex: PropTypes.bool,
  onAddPin: PropTypes.func,
  onUpdatePinLocation: PropTypes.func,
  onDeletePin: PropTypes.func,
  onFocusChange: PropTypes.func.isRequired,
  focusedPinIndex: PropTypes.number,
  propertyType: PropTypes.oneOf([COMMERCIAL, RESIDENTIAL]).isRequired,
  pinDragEnabled: PropTypes.bool,
  colors: PropTypes.arrayOf(PropTypes.string),
  mapDisclaimerOffsetHeight: PropTypes.number,
  defaultZoom: PropTypes.number,
  onFetchPinAddress: PropTypes.func,
};

export default BingMap;
